// CoTraderCustomTickerTape.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { getAuthenticatedWebSocketUrl } from "../../utilities/authHelper";
import { getRawTickerFromCatalogValue } from "../../utilities/marketSymbols";
import { useResolvedDark } from "../../ThemeProvider/ThemeContext";

const API_URL = process.env.REACT_APP_API_URL;

function numberFmt(n, maxFrac = 4) {
  if (n == null || !isFinite(n)) return "—";
  const abs = Math.abs(n);
  const frac = abs >= 1000 ? 2 : abs >= 100 ? 3 : abs >= 10 ? 4 : maxFrac;
  return n.toLocaleString(undefined, { maximumFractionDigits: frac });
}

function ensureLogo(q) {
  if (q?.logo) return q.logo;
  if (q?.domain) return `https://logo.clearbit.com/${q.domain}`;
  return null;
}

export default function CoTraderCustomTickerTape({
  symbols,
  symbolLabels = {},
  dark,
  onSymbolClick,
  className,
  style,
}) {
  const [quotesBySymbol, setQuotesBySymbol] = useState({});
  const [paused, setPaused] = useState(false);

  const containerRef = useRef(null);
  const innerRef = useRef(null);
  const animRef = useRef({
    offset: 0,
    lastTime: null,
    contentWidth: 0,
    rafId: null,
  });

  // pixels/second scroll speed
  const speedPxPerSec = 40;

  // Deduplicate symbols
  // Keyed by content so a new array with the same symbols doesn't reset the socket.
  const symbolsKey = (symbols || []).join("|");
  const syms = useMemo(() => {
    const set = new Set();
    return (symbols || [])
      .map((s) => getRawTickerFromCatalogValue(s))
      .filter((s) => s && !set.has(s) && set.add(s));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbolsKey]);
  const labelsBySymbol = useMemo(
    () =>
      Object.entries(symbolLabels || {}).reduce((accumulator, [symbol, label]) => {
        const rawSymbol = getRawTickerFromCatalogValue(symbol);
        const displayLabel = String(label || "").trim();

        if (rawSymbol && displayLabel) {
          accumulator[rawSymbol] = displayLabel;
          accumulator[rawSymbol.toLowerCase()] = displayLabel;
        }

        return accumulator;
      }, {}),
    [symbolLabels]
  );

  const isDark = useResolvedDark(dark);

  const theme = useMemo(
    () =>
      isDark
        ? {
            barBg: "rgba(15, 20, 36, 0.92)",
            border: "rgba(255,255,255,0.08)",
            text: "#E8ECF8",
            muted: "#A6B0CF",
            tileA: "rgba(255,255,255,0.02)",
            tileB: "rgba(255,255,255,0.05)",
            up: "#22c55e",
            down: "#ef4444",
          }
        : {
            barBg: "rgba(255,255,255,0.92)",
            border: "rgba(0,0,0,0.08)",
            text: "#111827",
            muted: "#374151",
            tileA: "#ffffff",
            tileB: "#f3f4f6",
            up: "#16a34a",
            down: "#dc2626",
          },
    [isDark]
  );

  // --- WebSocket: ONE connection with comma-separated symbols -------------
  useEffect(() => {
    if (!syms.length || !API_URL) return;

    setQuotesBySymbol({});

    const symbolString = syms.join(",");
    const chatId = `PRICE:${symbolString}`;
    const userId = "ticker-tape";
    let isCancelled = false;
    let ws;

    const openSocket = async () => {
      const wsUrl = await getAuthenticatedWebSocketUrl(chatId, userId);
      if (isCancelled) return;

      ws = new WebSocket(wsUrl);

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          const sym = data.symbol;
          if (!sym) return;

          const raw = data.raw || {};
          const changePercent =
            data.changePercent ??
            data.change_percent ??
            raw.change_percent ??
            null;

          setQuotesBySymbol((prev) => ({
            ...prev,
            [sym]: {
              ...prev[sym],
              ...data,
              changePercent,
            },
          }));
        } catch (err) {
          console.error("[TickerTape] WS parse error:", err);
        }
      };

      ws.onerror = (err) => console.error("[TickerTape] WS error:", err);
    };

    void openSocket();

    return () => {
      isCancelled = true;
      try {
        ws.close();
      } catch (_) {}
    };
  }, [syms]);

  // Build quotes array in fixed order
  const quotes = useMemo(
    () => syms.map((s) => quotesBySymbol[s]).filter(Boolean),
    [syms, quotesBySymbol]
  );

  // Duplicate for seamless loop
  const items = useMemo(() => [...quotes, ...quotes], [quotes]);

  // --- Animation: manual rAF-based scroll for smoothness ------------------
  useEffect(() => {
    const inner = innerRef.current;
    if (!inner || quotes.length === 0) {
      animRef.current.contentWidth = 0;
      animRef.current.offset = 0;
      return;
    }
    const fullWidth = inner.scrollWidth / 2;
    animRef.current.contentWidth = fullWidth;
  }, [quotes.length]);

  useEffect(() => {
    const inner = innerRef.current;
    if (!inner) return;

    const step = (timestamp) => {
      const state = animRef.current;
      if (state.lastTime == null) state.lastTime = timestamp;

      const dt = (timestamp - state.lastTime) / 1000;
      state.lastTime = timestamp;

      if (!paused && state.contentWidth > 0) {
        state.offset += speedPxPerSec * dt;
        if (state.offset >= state.contentWidth) state.offset -= state.contentWidth;
        inner.style.transform = `translateX(${-state.offset}px)`;
      }

      state.rafId = requestAnimationFrame(step);
    };

    animRef.current.lastTime = null;
    animRef.current.rafId = requestAnimationFrame(step);

    return () => {
      if (animRef.current.rafId != null) {
        cancelAnimationFrame(animRef.current.rafId);
        animRef.current.rafId = null;
      }
    };
  }, [paused]);

  return (
    <div
      ref={containerRef}
      className={className}
      style={{
        width: "100%",
        overflow: "hidden",
        borderTop: `1px solid ${theme.border}`,
        borderBottom: `1px solid ${theme.border}`,
        background: theme.barBg,
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
        ...style,
      }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div
        ref={innerRef}
        style={{
          display: "flex",
          whiteSpace: "nowrap",
          willChange: "transform",
        }}
      >
        {items.map((q, i) => {
          const logo = ensureLogo(q);
          const chgPct = q?.changePercent;
          const up = typeof chgPct === "number" ? chgPct >= 0 : null;
          const rawSymbol = getRawTickerFromCatalogValue(q?.symbol);
          const displaySymbol =
            labelsBySymbol[rawSymbol] ||
            labelsBySymbol[rawSymbol.toLowerCase()] ||
            q?.longName ||
            q?.shortName ||
            rawSymbol?.replace("=X", "") ||
            "—";

          const color =
            up == null ? theme.muted : up ? theme.up : theme.down;

          const tileBg = i % 2 === 0 ? theme.tileA : theme.tileB;

          return (
            <div
              key={`${q?.symbol || "sym"}-${i}`}
              onClick={() => onSymbolClick && q?.symbol && onSymbolClick(q.symbol)}
              title={displaySymbol}
              style={{
                width: 240,
                minWidth: 220,
                maxWidth: 240,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 10,
                padding: "8px 14px",
                borderRight: `1px solid ${theme.border}`,
                cursor: onSymbolClick ? "pointer" : "default",
                overflow: "hidden",
                boxSizing: "border-box",
                backgroundColor: tileBg,
              }}
            >
              {/* Left */}
              <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                {logo ? (
                  <img
                    src={logo}
                    alt=""
                    width={18}
                    height={18}
                    style={{ borderRadius: 999, objectFit: "contain" }}
                    onError={(e) => (e.currentTarget.style.display = "none")}
                  />
                ) : null}

                <span
                  style={{
                    fontWeight: 600,
                    fontSize: 13,
                    whiteSpace: "nowrap",
                    color: theme.text,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {displaySymbol}
                </span>
              </div>

              {/* Right */}
              <div
                style={{
                  display: "flex",
                  alignItems: "baseline",
                  gap: 8,
                  whiteSpace: "nowrap",
                }}
              >
                <span style={{ fontSize: 12, color: theme.text }}>
                  {numberFmt(q?.price)}
                </span>
                <span style={{ fontSize: 11, color, opacity: 0.95 }}>
                  {typeof chgPct === "number"
                    ? `${chgPct >= 0 ? "▲" : "▼"} ${numberFmt(chgPct, 2)}%`
                    : "—"}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
